// ─────────────────────────────────────────────────────────────────────────────
//  Available Loads — three co-located page components (pattern mirrors JobPages.tsx)
//    PostAvailableLoadPage  — carrier posts spare capacity
//    AvailableLoadsBoardPage — customer searches by postcode/map
//    AvailableLoadDetailPage — customer views detail and converts to job
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect, useRef, useCallback, type FormEvent } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import * as maplibregl from 'maplibre-gl';
import type { Feature } from 'geojson';
import { MapPin, Truck, Package, ChevronRight, X, List, Map as MapIcon, Search } from 'lucide-react';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient } from '../services/api';
import { VehicleType, AvailableLoadStatus, type AvailableLoadDto } from '../types';
import { MAP_STYLE_URL, createPinElement } from '../lib/mapLibre';

// ── Shared helpers ────────────────────────────────────────────────────────────

const VEHICLE_LABELS: Record<VehicleType, string> = {
  [VehicleType.Motorcycle]: 'Motorcycle',
  [VehicleType.Car]:        'Car',
  [VehicleType.SmallVan]:   'Small Van',
  [VehicleType.MidiVan]:    'Midi Van',
  [VehicleType.LWBVan]:     'LWB Van',
  [VehicleType.LutonVan]:   'Luton Van',
  [VehicleType.Tonne7_5]:   '7.5T',
  [VehicleType.Tonne12]:    '12T',
  [VehicleType.Tonne18]:    '18T',
  [VehicleType.Tonne26]:    '26T',
  [VehicleType.Artic]:      'Artic',
};

const CARRIER_VEHICLE_TYPES: VehicleType[] = [
  VehicleType.SmallVan, VehicleType.MidiVan, VehicleType.LWBVan,
  VehicleType.LutonVan, VehicleType.Tonne7_5, VehicleType.Tonne12,
  VehicleType.Tonne18, VehicleType.Tonne26, VehicleType.Artic,
];

const STATUS_LABEL: Record<AvailableLoadStatus, string> = {
  [AvailableLoadStatus.Active]:    'Active',
  [AvailableLoadStatus.Converted]: 'Booked',
  [AvailableLoadStatus.Expired]:   'Expired',
  [AvailableLoadStatus.Cancelled]: 'Cancelled',
};

const RADIUS_OPTIONS = [10, 25, 50, 100];

async function geocodePostcode(postcode: string): Promise<{ lat: number; lng: number } | null> {
  try {
    const r = await fetch(
      `https://api.postcodes.io/postcodes/${encodeURIComponent(postcode.trim().replace(/\s+/g, ''))}`
    );
    const d = await r.json();
    if (d.result?.latitude) return { lat: d.result.latitude, lng: d.result.longitude };
  } catch {}
  return null;
}

interface PostcodeInputProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onGeocode: (lat: number, lng: number) => void;
  placeholder?: string;
}

function PostcodeInput({ label, value, onChange, onGeocode, placeholder = 'Start typing a postcode…' }: PostcodeInputProps) {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [open, setOpen]               = useState(false);
  const wrapRef                        = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const q = value.replace(/\s/g, '');
    if (q.length < 2) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(q)}/autocomplete`);
        const d = await r.json();
        setSuggestions(d.result ?? []);
        setOpen(true);
      } catch { setSuggestions([]); }
    }, 350);
    return () => clearTimeout(t);
  }, [value]);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const select = async (pc: string) => {
    setOpen(false);
    setSuggestions([]);
    onChange(pc);
    const result = await geocodePostcode(pc);
    if (result) onGeocode(result.lat, result.lng);
  };

  return (
    <div ref={wrapRef} className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          autoCapitalize="characters"
          autoComplete="off"
          className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
          {suggestions.slice(0, 6).map(s => (
            <button
              key={s}
              type="button"
              onMouseDown={() => select(s)}
              className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  1. POST AVAILABLE LOAD PAGE — carrier
// ─────────────────────────────────────────────────────────────────────────────
export function PostAvailableLoadPage() {
  const navigate              = useNavigate();
  const { getToken }          = useAppAuth();
  const [submitting, setSub]  = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const [vehicleType, setVehicle]     = useState<VehicleType>(VehicleType.LutonVan);
  const [pickupAddress, setPickupAddr] = useState('');
  const [pickupLat, setPickupLat]     = useState(0);
  const [pickupLng, setPickupLng]     = useState(0);
  const [deliveryAddress, setDelAddr] = useState('');
  const [deliveryLat, setDelLat]      = useState(0);
  const [deliveryLng, setDelLng]      = useState(0);
  const [availablePallets, setAvail]  = useState('');
  const [totalPallets, setTotal]      = useState('');
  const [availableWeightKg, setWt]    = useState('');
  const [notes, setNotes]             = useState('');
  const [availableFrom, setFrom]      = useState(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() + 60, 0, 0);
    return d.toISOString().slice(0, 16);
  });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const avail = parseInt(availablePallets, 10);
    if (!pickupAddress.trim() || pickupLat === 0) { setError('Pickup location is required — select a suggestion.'); return; }
    if (!deliveryAddress.trim() || deliveryLat === 0) { setError('Delivery location is required — select a suggestion.'); return; }
    if (!avail || avail <= 0) { setError('Available pallets must be a positive number.'); return; }

    setSub(true);
    try {
      const client = createApiClient(getToken);
      await client.post('/api/v1/available-loads', {
        vehicleType,
        pickupAddress,
        pickupLat,
        pickupLng,
        deliveryAddress,
        deliveryLat,
        deliveryLng,
        availableFrom:       new Date(availableFrom).toISOString(),
        availablePallets:    avail,
        totalCapacityPallets: totalPallets ? parseInt(totalPallets, 10) : undefined,
        availableWeightKg:   availableWeightKg ? parseFloat(availableWeightKg) : undefined,
        notes:               notes.trim() || undefined,
      });
      navigate('/available-loads/mine');
    } catch (ex: any) {
      setError(ex.response?.data?.error ?? ex.message ?? 'Submission failed.');
    } finally {
      setSub(false);
    }
  };

  return (
    <div className="page-shell">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Post Available Load</h1>
        <p className="text-sm text-gray-500 mt-1">
          Share your spare pallet space on your planned route. Customers can find it on the map and convert it to a biddable job.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Vehicle */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Vehicle Type</label>
          <div className="flex flex-wrap gap-2">
            {CARRIER_VEHICLE_TYPES.map(vt => (
              <button
                key={vt}
                type="button"
                onClick={() => setVehicle(vt)}
                className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                  vehicleType === vt
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {VEHICLE_LABELS[vt]}
              </button>
            ))}
          </div>
        </div>

        {/* Route */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
          <h2 className="font-semibold text-gray-800">Route</h2>
          <PostcodeInput
            label="Pickup postcode"
            value={pickupAddress}
            onChange={setPickupAddr}
            onGeocode={(lat, lng) => { setPickupLat(lat); setPickupLng(lng); }}
          />
          <PostcodeInput
            label="Delivery postcode"
            value={deliveryAddress}
            onChange={setDelAddr}
            onGeocode={(lat, lng) => { setDelLat(lat); setDelLng(lng); }}
          />
        </div>

        {/* Capacity */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
          <h2 className="font-semibold text-gray-800">Spare Capacity</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Available pallets <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                value={availablePallets}
                onChange={e => setAvail(e.target.value)}
                placeholder="e.g. 6"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total capacity (optional)</label>
              <input
                type="number"
                min="1"
                value={totalPallets}
                onChange={e => setTotal(e.target.value)}
                placeholder="e.g. 10"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max weight (kg, optional)</label>
            <input
              type="number"
              value={availableWeightKg}
              onChange={e => setWt(e.target.value)}
              placeholder="e.g. 1200"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Timing */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Collecting from <span className="text-red-500">*</span>
          </label>
          <input
            type="datetime-local"
            value={availableFrom}
            onChange={e => setFrom(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
          <textarea
            rows={3}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Restrictions, e.g. no hazardous goods, no oversized items…"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 flex items-start gap-2">
            <X className="w-4 h-4 mt-0.5 shrink-0" /> {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold py-3 rounded-xl transition-colors text-sm"
        >
          {submitting ? 'Posting…' : 'Post Available Load'}
        </button>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  2. AVAILABLE LOADS BOARD — customer searches by postcode
// ─────────────────────────────────────────────────────────────────────────────
export function AvailableLoadsBoardPage() {
  const { getToken }                    = useAppAuth();
  const [postcode, setPostcode]         = useState('');
  const [radius, setRadius]             = useState(25);
  const [coords, setCoords]             = useState<{ lat: number; lng: number } | null>(null);
  const [searchedPc, setSearchedPc]    = useState('');
  const [loads, setLoads]               = useState<AvailableLoadDto[]>([]);
  const [loading, setLoading]           = useState(false);
  const [postcodeError, setPostcodeError] = useState('');
  const [view, setView]                 = useState<'list' | 'map'>('list');
  const [geocoding, setGeocoding]       = useState(false);
  const mapRef                           = useRef<maplibregl.Map | null>(null);
  const mapLoadedRef                     = useRef(false);
  const markersRef                       = useRef<maplibregl.Marker[]>([]);
  const containerRef                     = useRef<HTMLDivElement>(null);

  const fetchLoads = useCallback(async (lat: number, lng: number, r: number) => {
    setLoading(true);
    try {
      const client = createApiClient(getToken);
      const res = await client.get<AvailableLoadDto[]>('/api/v1/available-loads/feed', {
        params: { lat, lng, radius: r },
      });
      setLoads(res.data);
    } catch {
      setLoads([]);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  const handleSearch = async (e: FormEvent) => {
    e.preventDefault();
    setPostcodeError('');
    setGeocoding(true);
    const result = await geocodePostcode(postcode);
    setGeocoding(false);
    if (result) {
      setCoords(result);
      setSearchedPc(postcode.toUpperCase().trim());
      fetchLoads(result.lat, result.lng, radius);
    } else {
      setPostcodeError('Postcode not found — try a full UK postcode, e.g. LU4 9AN');
    }
  };

  useEffect(() => {
    if (coords) fetchLoads(coords.lat, coords.lng, radius);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radius]);

  // Map initialisation
  useEffect(() => {
    if (view !== 'map' || !containerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE_URL,
      center: [-1.5, 52.5],
      zoom: 6,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }));

    map.on('load', () => {
      map.addSource('available-loads-routes', {
        type: 'geojson', data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'available-loads-routes', type: 'line', source: 'available-loads-routes',
        paint: { 'line-color': '#0f766e', 'line-width': 2, 'line-dasharray': [3, 2.5] },
      });
      mapLoadedRef.current = true;
      renderLoadsOnMap();
    });

    return () => {
      mapLoadedRef.current = false;
      mapRef.current?.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const renderLoadsOnMap = useCallback(() => {
    const map = mapRef.current;
    if (!map || !mapLoadedRef.current) return;

    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    const color = '#0f766e';
    const bounds = new maplibregl.LngLatBounds();
    const features: Feature[] = [];
    let hasPoints = false;

    loads.forEach(load => {
      if (!load.pickupLat || !load.deliveryLat) return;
      hasPoints = true;

      features.push({
        type: 'Feature', properties: {},
        geometry: {
          type: 'LineString',
          coordinates: [[load.pickupLng, load.pickupLat], [load.deliveryLng, load.deliveryLat]],
        },
      });

      const palletLabel = load.totalCapacityPallets
        ? `${load.availablePallets}/${load.totalCapacityPallets}`
        : `${load.availablePallets}`;
      const tooltip = `<div style="min-width:160px;font-size:12px"><div style="font-weight:700">${VEHICLE_LABELS[load.vehicleType]} · ${palletLabel} pallets</div><div style="color:#6b7280">${load.pickupAddress.split(',')[0]} → ${load.deliveryAddress.split(',')[0]}</div><div style="color:#6b7280">${load.distanceMiles.toFixed(1)} mi</div></div>`;
      const popup = new maplibregl.Popup({ offset: 14 }).setHTML(tooltip);

      const pMarker = new maplibregl.Marker({ element: createPinElement('P', color) })
        .setLngLat([load.pickupLng, load.pickupLat]).setPopup(popup).addTo(map);
      const dMarker = new maplibregl.Marker({ element: createPinElement('D', color) })
        .setLngLat([load.deliveryLng, load.deliveryLat]).setPopup(new maplibregl.Popup({ offset: 14 }).setHTML(tooltip)).addTo(map);
      markersRef.current.push(pMarker, dMarker);

      bounds.extend([load.pickupLng, load.pickupLat]);
      bounds.extend([load.deliveryLng, load.deliveryLat]);
    });

    (map.getSource('available-loads-routes') as maplibregl.GeoJSONSource)
      ?.setData({ type: 'FeatureCollection', features });

    if (hasPoints) {
      map.fitBounds(bounds, { padding: 40 });
      setTimeout(() => map.resize(), 200);
    }
  }, [loads]);

  useEffect(() => {
    if (view === 'map') renderLoadsOnMap();
  }, [loads, view, renderLoadsOnMap]);

  return (
    <div className="flex h-[calc(100vh-64px)] overflow-hidden bg-gray-50">
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">

        {/* Header */}
        <div className="shrink-0 px-4 py-3 bg-white border-b border-gray-200 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-bold text-gray-900">Available Loads</h1>
            <div className="flex rounded-lg border border-gray-200 overflow-hidden">
              {(['list', 'map'] as const).map(v => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  className={`px-3 py-1.5 flex items-center gap-1 text-sm transition-colors ${view === v ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  {v === 'list' ? <List className="w-4 h-4" /> : <MapIcon className="w-4 h-4" />}
                  <span className="hidden sm:inline capitalize">{v}</span>
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleSearch} className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              <input
                className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                placeholder="Your pickup postcode, e.g. LU4 9AN"
                value={postcode}
                onChange={e => { setPostcode(e.target.value); setPostcodeError(''); }}
              />
            </div>
            <select
              className="appearance-none border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
              value={radius}
              onChange={e => setRadius(Number(e.target.value))}
            >
              {RADIUS_OPTIONS.map(r => <option key={r} value={r}>{r} miles</option>)}
            </select>
            <button
              type="submit"
              disabled={geocoding || !postcode.trim()}
              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
            >
              <Search className="w-3.5 h-3.5" />
              {geocoding ? 'Searching…' : 'Search'}
            </button>
            {coords && !loading && (
              <p className="text-xs text-gray-400">
                {loads.length} load{loads.length !== 1 ? 's' : ''} within {radius} mi of {searchedPc}
              </p>
            )}
          </form>
          {postcodeError && <p className="text-xs text-red-600">{postcodeError}</p>}
        </div>

        {/* Body */}
        {!coords ? (
          <div className="flex-1 flex items-center justify-center text-center p-6">
            <div>
              <Package className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-500">Enter a postcode to find available loads near you</p>
              <p className="text-xs text-gray-400 mt-1">Carriers with spare capacity will appear here</p>
            </div>
          </div>
        ) : loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              Loading…
            </div>
          </div>
        ) : view === 'list' ? (
          <div className="flex-1 overflow-auto">
            {loads.length === 0 ? (
              <div className="flex items-center justify-center h-full text-center">
                <div>
                  <Package className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No available loads found nearby</p>
                  <p className="text-xs text-gray-400 mt-1">Try a larger search radius</p>
                </div>
              </div>
            ) : (
              <table className="w-full text-sm border-collapse">
                <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-medium text-gray-600 text-xs">Vehicle</th>
                    <th className="text-left px-4 py-2.5 font-medium text-gray-600 text-xs hidden md:table-cell">Route</th>
                    <th className="text-right px-4 py-2.5 font-medium text-gray-600 text-xs">Pallets</th>
                    <th className="text-left px-4 py-2.5 font-medium text-gray-600 text-xs hidden sm:table-cell">Available From</th>
                    <th className="text-right px-4 py-2.5 font-medium text-gray-600 text-xs hidden sm:table-cell">Distance</th>
                    <th className="w-10 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {loads.map(load => (
                    <tr key={load.id} className="hover:bg-gray-50/60 transition-colors">
                      <td className="px-4 py-3 align-middle">
                        <div className="flex items-center gap-2">
                          <Truck className="w-4 h-4 text-teal-600 shrink-0" />
                          <span className="font-medium text-gray-900 text-xs">{VEHICLE_LABELS[load.vehicleType]}</span>
                        </div>
                        {load.carrierName && <p className="text-xs text-gray-400 mt-0.5 pl-6">{load.carrierName}</p>}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell align-middle">
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <span className="truncate max-w-[100px]">{load.pickupAddress.split(',')[0]}</span>
                          <ChevronRight className="w-3 h-3 shrink-0 text-gray-300" />
                          <span className="truncate max-w-[100px]">{load.deliveryAddress.split(',')[0]}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right align-middle">
                        <div className="inline-flex flex-col items-end">
                          <span className="text-xs font-bold text-teal-700">{load.availablePallets}</span>
                          {load.totalCapacityPallets && (
                            <span className="text-[10px] text-gray-400">of {load.totalCapacityPallets}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell align-middle">
                        <span className="text-xs text-gray-600">
                          {new Date(load.availableFrom).toLocaleString('en-GB', {
                            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                          })}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right hidden sm:table-cell align-middle">
                        <div className="text-xs text-gray-600">{load.distanceMiles.toFixed(1)} mi</div>
                        {load.distanceFromSearchMiles != null && (
                          <div className="text-[10px] text-gray-400">{load.distanceFromSearchMiles.toFixed(1)} mi away</div>
                        )}
                      </td>
                      <td className="pr-4 py-3 text-center align-middle">
                        <Link
                          to={`/available-loads/${load.id}`}
                          className="inline-flex p-1.5 rounded-md text-gray-400 hover:text-teal-600 hover:bg-teal-50 transition-colors"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          /* MAP VIEW */
          <div className="flex-1 relative overflow-hidden">
            {loads.length === 0 ? (
              <div className="flex items-center justify-center h-full text-center">
                <Package className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No available loads to show on map</p>
              </div>
            ) : (
              <div ref={containerRef} className="w-full h-full" />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  3. AVAILABLE LOAD DETAIL PAGE — customer views + converts
// ─────────────────────────────────────────────────────────────────────────────
export function AvailableLoadDetailPage() {
  const { id }                   = useParams<{ id: string }>();
  const navigate                 = useNavigate();
  const { getToken }             = useAppAuth();
  const [load, setLoad]          = useState<AvailableLoadDto | null>(null);
  const [loadingLoad, setLL]     = useState(true);
  const [showForm, setShowForm]  = useState(false);
  const [submitting, setSub]     = useState(false);
  const [error, setError]        = useState<string | null>(null);
  const [title, setTitle]        = useState('');
  const [goodsDesc, setGoodsDesc]= useState('');
  const [estValue, setEstValue]  = useState('');
  const [scheduledAt, setSched]  = useState(() => {
    const d = new Date(); d.setMinutes(d.getMinutes() + 90, 0, 0);
    return d.toISOString().slice(0, 16);
  });

  useEffect(() => {
    if (!id) return;
    const client = createApiClient(getToken);
    client.get<AvailableLoadDto>(`/api/v1/available-loads/${id}`)
      .then(r => setLoad(r.data))
      .catch(() => {})
      .finally(() => setLL(false));
  }, [id, getToken]);

  const handleConvert = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { setError('Job title is required.'); return; }
    if (!goodsDesc.trim()) { setError('Goods description is required.'); return; }
    setError(null);
    setSub(true);
    try {
      const client = createApiClient(getToken);
      const res = await client.post<{ jobId: string; message: string }>(
        `/api/v1/available-loads/${id}/convert`,
        {
          title:             title.trim(),
          goodsDescription:  goodsDesc.trim(),
          scheduledAt:       new Date(scheduledAt).toISOString(),
          estimatedValueGbp: estValue ? parseFloat(estValue) : undefined,
        }
      );
      navigate(`/jobs/${res.data.jobId}`);
    } catch (ex: any) {
      setError(ex.response?.data?.error ?? ex.message ?? 'Conversion failed.');
    } finally {
      setSub(false);
    }
  };

  if (loadingLoad) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full" />
    </div>
  );

  if (!load) return (
    <div className="page-shell text-center text-gray-500">Available load not found.</div>
  );

  const isActive = load.status === AvailableLoadStatus.Active;
  const palletLabel = load.totalCapacityPallets
    ? `${load.availablePallets} of ${load.totalCapacityPallets} pallet spaces`
    : `${load.availablePallets} pallet spaces`;

  return (
    <div className="page-shell space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Available Load</h1>
          <p className="text-sm text-gray-500 mt-1">
            {VEHICLE_LABELS[load.vehicleType]}
            {load.carrierName ? ` · ${load.carrierName}` : ''}
          </p>
        </div>
        <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${
          isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
        }`}>
          {STATUS_LABEL[load.status]}
        </span>
      </div>

      {/* Route */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="font-semibold text-gray-800 mb-4">Route</h2>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
              <MapPin className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Pickup</p>
              <p className="text-sm font-medium text-gray-900">{load.pickupAddress}</p>
            </div>
          </div>
          <div className="ml-3.5 w-0.5 h-5 bg-gray-200" />
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center shrink-0">
              <MapPin className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Delivery</p>
              <p className="text-sm font-medium text-gray-900">{load.deliveryAddress}</p>
            </div>
          </div>
          <p className="text-xs text-gray-500 pt-2 border-t border-gray-50">{load.distanceMiles.toFixed(1)} mile route</p>
        </div>
      </div>

      {/* Capacity */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="font-semibold text-gray-800 mb-3">Capacity</h2>
        <div className="flex items-center gap-3">
          <Package className="w-5 h-5 text-teal-600 shrink-0" />
          <span className="text-base font-semibold text-teal-700">{palletLabel}</span>
        </div>
        {load.availableWeightKg && (
          <p className="text-sm text-gray-600 mt-2">Up to {load.availableWeightKg} kg</p>
        )}
        {load.availableLoadingMetresLdm && (
          <p className="text-sm text-gray-600 mt-1">{load.availableLoadingMetresLdm} LDM</p>
        )}
      </div>

      {/* Timing */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="font-semibold text-gray-800 mb-2">Availability</h2>
        <p className="text-sm text-gray-700">
          From{' '}
          <span className="font-medium">
            {new Date(load.availableFrom).toLocaleString('en-GB', {
              weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
              hour: '2-digit', minute: '2-digit',
            })}
          </span>
        </p>
        {load.availableUntil && (
          <p className="text-sm text-gray-500 mt-1">
            Until {new Date(load.availableUntil).toLocaleString('en-GB', {
              day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
            })}
          </p>
        )}
      </div>

      {load.notes && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-amber-800 mb-1">Notes from carrier</p>
          <p className="text-sm text-amber-700">{load.notes}</p>
        </div>
      )}

      {/* Converted indicator */}
      {!isActive && load.convertedJobId && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-gray-600 mb-1">Already booked</p>
          <Link to={`/jobs/${load.convertedJobId}`} className="text-sm text-blue-600 hover:underline">
            View the job →
          </Link>
        </div>
      )}

      {/* CTA */}
      {isActive && !showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-3 rounded-xl transition-colors text-sm"
        >
          Book This Space — Convert to Job
        </button>
      )}

      {isActive && showForm && (
        <form onSubmit={handleConvert} className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
          <div>
            <h2 className="font-semibold text-gray-800 mb-1">Your Goods Details</h2>
            <p className="text-xs text-gray-500">
              The carrier's route and vehicle type are pre-filled. All carriers (including the original poster) can quote on this job.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job title <span className="text-red-500">*</span>
            </label>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Engine parts — Leeds to Bradford"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Goods description <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={3}
              value={goodsDesc}
              onChange={e => setGoodsDesc(e.target.value)}
              placeholder="Describe your goods, handling requirements…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estimated value (£)</label>
              <input
                type="number"
                value={estValue}
                onChange={e => setEstValue(e.target.value)}
                placeholder="Optional"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Scheduled date/time <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={e => setSched(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 flex items-start gap-2">
              <X className="w-4 h-4 mt-0.5 shrink-0" /> {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => { setShowForm(false); setError(null); }}
              className="flex-1 border border-gray-300 text-gray-600 font-semibold py-2.5 rounded-xl text-sm hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white font-semibold py-2.5 rounded-xl text-sm transition-colors"
            >
              {submitting ? 'Creating job…' : 'Create Job'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  4. MY AVAILABLE LOADS — carrier's own listings
// ─────────────────────────────────────────────────────────────────────────────
export function MyAvailableLoadsPage() {
  const { getToken }              = useAppAuth();
  const navigate                  = useNavigate();
  const [loads, setLoads]         = useState<AvailableLoadDto[]>([]);
  const [loading, setLoading]     = useState(true);
  const [cancelling, setCancelling] = useState<string | null>(null);

  const fetchLoads = useCallback(async () => {
    const client = createApiClient(getToken);
    try {
      const res = await client.get<AvailableLoadDto[]>('/api/v1/available-loads/mine');
      setLoads(res.data);
    } catch {}
    finally { setLoading(false); }
  }, [getToken]);

  useEffect(() => { fetchLoads(); }, [fetchLoads]);

  const handleCancel = async (id: string) => {
    if (!window.confirm('Cancel this listing?')) return;
    setCancelling(id);
    try {
      const client = createApiClient(getToken);
      await client.delete(`/api/v1/available-loads/${id}`);
      setLoads(prev => prev.filter(l => l.id !== id));
    } catch (e: any) {
      alert(e.response?.data?.error ?? 'Could not cancel listing.');
    } finally { setCancelling(null); }
  };

  return (
    <div className="page-shell">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My Available Loads</h1>
        <button
          onClick={() => navigate('/available-loads/post')}
          className="bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors"
        >
          + Post Load
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin w-6 h-6 border-4 border-teal-500 border-t-transparent rounded-full" />
        </div>
      ) : loads.length === 0 ? (
        <div className="text-center py-16">
          <Package className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No available loads posted yet.</p>
          <button
            onClick={() => navigate('/available-loads/post')}
            className="mt-4 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"
          >
            Post Your First Load
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {loads.map(load => (
            <div key={load.id} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-semibold text-gray-900 text-sm">
                    {VEHICLE_LABELS[load.vehicleType]} · {load.availablePallets} pallets
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {new Date(load.availableFrom).toLocaleString('en-GB', {
                      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                    })}
                  </p>
                </div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                  load.status === AvailableLoadStatus.Active    ? 'bg-green-100 text-green-800'  :
                  load.status === AvailableLoadStatus.Converted ? 'bg-blue-100 text-blue-800'    :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {STATUS_LABEL[load.status]}
                </span>
              </div>
              <p className="text-xs text-gray-600 truncate">
                {load.pickupAddress} → {load.deliveryAddress}
              </p>
              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-50">
                {load.convertedJobId && (
                  <Link to={`/jobs/${load.convertedJobId}`} className="text-xs text-blue-600 hover:underline font-medium">
                    View converted job →
                  </Link>
                )}
                {load.status === AvailableLoadStatus.Active && (
                  <button
                    onClick={() => handleCancel(load.id)}
                    disabled={cancelling === load.id}
                    className="text-xs text-red-600 hover:text-red-700 font-medium disabled:opacity-50"
                  >
                    {cancelling === load.id ? 'Cancelling…' : 'Cancel listing'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
